import { create } from 'zustand';
import { PRIMARY_ASSET, type AssetId } from '@/core/project/sequence';
import { engine } from '@/engine/client';
import type { MediaInfo } from '@/engine/types';
import { reopen } from '@/lib/file-handles';
import { projectDb } from '@/lib/project-db';
import { projectRecording, removeProjectRecording } from '@/lib/recording/sessions';
import { useEditorStore, type RecordingOrigin } from '../editor-store';
import { filesInMemory, forgetFiles } from './autosave';
import { matchFiles, neededAssets, type ProjectSummary, type SavedAsset, type SavedProject } from './project';

/** A project being picked up again: what it needs, and what is in hand so far. */
export interface Continuing {
  readonly project: SavedProject;
  readonly needed: readonly SavedAsset[];
  readonly files: ReadonlyMap<AssetId, File>;
  /** The recording the project kept on the device, when it still has it. */
  readonly recording: { readonly screen: File; readonly camera: File | null } | null;
  readonly matching: boolean;
  /** Picked files that are none of the project's. */
  readonly strangers: readonly string[];
}

interface ProjectsStore {
  /** Newest first; null until read (or when the device keeps none). */
  readonly projects: readonly ProjectSummary[] | null;
  readonly continuing: Continuing | null;
  /** The project whose files are being looked for where they were, before it opens or asks for any. */
  readonly reaching: string | null;
  refresh(): Promise<void>;
  remove(id: string): Promise<void>;
  /** Starts picking a project up again; opens it straight away when nothing has to be picked. */
  continue(id: string): Promise<void>;
  /** Matches picked files to what the project still needs. */
  provide(files: readonly File[]): Promise<void>;
  /** Opens the project once every file it needs is in hand. */
  open(): Promise<void>;
  cancel(): void;
}

const missingOf = (continuing: Pick<Continuing, 'needed' | 'files'>): SavedAsset[] => continuing.needed.filter((asset) => !continuing.files.has(asset.id));

const identify = async (file: File) => (await engine.probe(file).promise) as MediaInfo;

/**
 * The files a project's kept handles still lead to, checked to be the ones it
 * was made with (a file replaced under the same name is not). The browser asks
 * the person once, right after their tap on Continue; after a "no" it isn't
 * asked again, and those files are picked by hand as before.
 */
async function filesWhereTheyWere(assets: readonly SavedAsset[]): Promise<Map<AssetId, File>> {
  const found = new Map<AssetId, File>();
  let ask = true;
  for (const asset of assets) {
    if (!asset.handle) continue;
    const reached = await reopen(asset.handle, { ask });
    if (!reached.file) {
      if (reached.refused) ask = false;
      continue;
    }
    const same = (await matchFiles([asset], [reached.file], identify)).get(asset.id);
    if (same) found.set(asset.id, same);
  }
  return found;
}

async function openProject({ project, files, recording }: Continuing): Promise<void> {
  const primary = files.get(PRIMARY_ASSET);
  if (!primary) return;
  // A recording opens with its camera layer; the project already holds its retakes as cuts.
  const origin: RecordingOrigin | undefined = recording
    ? { id: project.recordingId, camera: recording.camera, cuts: [], release: () => undefined, exported: () => undefined }
    : undefined;
  await useEditorStore.getState().openFile(primary, { restore: { project, files }, recording: origin });
}

/** Saved projects for the home page, and opening one again. */
export const useProjectsStore = create<ProjectsStore>()((set, get) => ({
  projects: null,
  continuing: null,
  reaching: null,

  async refresh() {
    try {
      set({ projects: await projectDb.list() });
    } catch {
      set({ projects: [] });
    }
  },

  async remove(id) {
    const summary = get().projects?.find((project) => project.id === id);
    await projectDb.remove(id).catch(() => undefined);
    forgetFiles(id);
    if (summary?.recordingId) await removeProjectRecording(summary.recordingId);
    await get().refresh();
  },

  async continue(id) {
    const project = await projectDb.get(id).catch(() => null);
    if (!project) return void (await get().refresh());
    const needed = neededAssets(project);
    const files = new Map<AssetId, File>();
    // Files from this page load are still in hand; a recording made here is on the device.
    for (const [assetId, file] of filesInMemory(id)) if (needed.some((asset) => asset.id === assetId)) files.set(assetId, file);
    const recording = project.recordingId ? await projectRecording(project.recordingId) : null;
    if (recording) files.set(PRIMARY_ASSET, recording.screen);
    // Then where the files were on the disk, where the browser kept the way there.
    const unreached = needed.filter((asset) => !files.has(asset.id));
    if (unreached.some((asset) => asset.handle)) {
      set({ reaching: id });
      try {
        for (const [assetId, file] of await filesWhereTheyWere(unreached)) files.set(assetId, file);
      } finally {
        set({ reaching: null });
      }
    }
    const continuing: Continuing = { project, needed, files, recording, matching: false, strangers: [] };
    // Nothing to pick: straight into the editor, without showing the picking step.
    if (missingOf(continuing).length === 0) return openProject(continuing);
    set({ continuing });
  },

  async provide(picked) {
    const continuing = get().continuing;
    if (!continuing || picked.length === 0) return;
    set({ continuing: { ...continuing, matching: true, strangers: [] } });
    const matched = await matchFiles(missingOf(continuing), picked, identify);
    const current = get().continuing;
    if (current?.project.id !== continuing.project.id) return;
    const files = new Map([...current.files, ...matched]);
    const used = new Set(matched.values());
    set({ continuing: { ...current, files, matching: false, strangers: picked.filter((file) => !used.has(file)).map((file) => file.name) } });
  },

  async open() {
    const continuing = get().continuing;
    if (!continuing || missingOf(continuing).length > 0) return;
    set({ continuing: null });
    await openProject(continuing);
  },

  cancel() {
    set({ continuing: null });
  },
}));

export { missingOf };
